import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/**
 * The in-flight state from the nine-state contract (Frontend Standards v1.1
 * §6): "Spinner replaces the label; disabled at original width", whose named
 * common mistake is "Letting the control resize, so layout jumps".
 *
 * The label is kept in the layout and only faded out — that is what pins the
 * width, with no measurement and no `min-width` for a caller to keep in sync.
 * The spinner is absolutely positioned over the top so it costs no width of
 * its own.
 *
 * `opacity-0` and NOT `invisible`/`hidden`: `visibility: hidden` would take
 * the label out of the accessibility tree along with the layout, leaving the
 * button with no accessible name for as long as the action ran. Opacity hides
 * it from sight only, so the name survives and `aria-busy` is what tells a
 * screen reader the control is working. `disabled` is what stops a second
 * submit (§6.1 rule 2, §9.1 rule 4).
 *
 * NOTE ON THE STANDARD: §6 says the spinner *replaces* the label, while §6.1
 * and §9.1 rule 4 say the label becomes the present participle ("Saving…").
 * Only §6's reading mechanically guarantees the no-resize rule both of them
 * ask for, so that is the one implemented. A caller that swaps its own label
 * to the participle still gets the right width — the participle is what the
 * hidden label measures.
 *
 * Not applicable with `asChild`: the child owns its own subtree, so there is
 * nothing to hide or overlay. Passing both is a no-op on the spinner and is
 * flagged by the type below.
 */
function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';
  const isLoading = loading && !asChild;

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={isLoading ? 'true' : undefined}
      aria-busy={isLoading || undefined}
      disabled={disabled || isLoading}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {isLoading ? (
        <>
          {/* `contents` would collapse the wrapper and lose the gap between a
              leading icon and its label, so the wrapper keeps the button's own
              flex layout instead of dissolving into it. */}
          <span className="inline-flex items-center gap-2 opacity-0">{children}</span>
          <Loader2 className="absolute size-4 animate-spin" aria-hidden="true" />
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
